import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { Location as DineEngineLocation } from '../../../../../interfaces/location.interface';
import { GeocodingService } from '../../../services/geocoding.service';
import { Geolocation } from '@capacitor/geolocation';
import { DistanceConfirmationComponent } from '@modules/locations/components';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocationService } from '../../../../../services/vendor-config-service/location.service';
import moment from 'moment-timezone';
import { TimeFrame } from '../../../../../interfaces/time-frame.interface';
import { HandoffType } from '../../../../../interfaces/handoff-type.enum';
import { Select } from '@ngxs/store';
import { GlobalStateModel } from 'src/store/state.model';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-mobile-location-card',
  templateUrl: './mobile-location-card.component.html',
  styleUrls: ['./mobile-location-card.component.scss'],
})
export class MobileLocationCardComponent implements OnInit {
  @Select((state: GlobalStateModel) => state.order.brandHandoffs) brandHandoffs$: Observable<{ handoff: HandoffType; label: string }[]>;
  location: DineEngineLocation;
  handoffs = HandoffType;
  isLoading = true;

  @Input() locationID: string;
  @Input() isCurrentLocation = false;
  @Input() displayInfoButton = true;
  @Input() displayOrderButton = true;
  @Input() displayCateringButton = false;
  @Input() pageFrom: string;
  @Input() editHandoff;
  @Input() locationLoading;
  @Input() cardEmphasis: boolean;
  @Input() locationList = true;

  @Output() orderNowButtonClick = new EventEmitter<DineEngineLocation>();
  @Output() locationInfoButtonClick = new EventEmitter<DineEngineLocation>();
  @Output() hoveredIndex = new EventEmitter<number>();
  @Output() leftCard = new EventEmitter<void>();

  pickupStart: Date;
  pickupEnd: Date;
  deliveryStart: Date;
  deliveryEnd: Date;
  handoffMethods: string;
  unavailable: string;
  distance: number;

  supportedHandoffTypes: { type: HandoffType; available: boolean }[] = [];
  unavailableHandoffTypes: HandoffType[] = [];

  constructor(
    private geo: GeocodingService,
    private modalService: NgbModal,
    private locationService: LocationService
  ) {}

  ngOnInit() {
    if (this.locationID) {
      this.locationService.getService().subscribe(service => {
        if (service) {
          service.getLocation(this.locationID).subscribe((location: DineEngineLocation) => {
            this.location = location;
            this.setTimes();
            this.determineHandoff();
            if (this.pageFrom === 'locations') {
              this.distance = parseInt(sessionStorage.getItem(this.location.locationID), 10);
            }
            this.supportedHandoffTypes = this.getSupportedHandoffTypes();
            this.unavailableHandoffTypes = this.getUnavailableHandoffTypes();
            this.isLoading = false;
          });
        }
      });
    }
  }

  orderNowButtonClicked(event: MouseEvent): void {
    event.stopPropagation();
    if (this.location.isLive) {
      if (this.distance > 25) {
        const modalRef = this.modalService.open(DistanceConfirmationComponent, { centered: true, modalDialogClass: 'condensed-modal' });
        modalRef.componentInstance.distance = this.distance;
        modalRef.componentInstance.continueEmit.subscribe(() => {
          this.orderNowButtonClick.emit(this.location);
          this.modalService.dismissAll();
        });
      } else {
        this.orderNowButtonClick.emit(this.location);
      }
    }
  }

  locationInfoButtonClicked(event: MouseEvent): void {
    event.stopPropagation();
    if (this.location.supportsPickup || this.location.supportsCurbside) {
      this.locationInfoButtonClick.emit(this.location);
    }
  }

  private setTimes() {
    if (!this.location) {
      return;
    }
    const now = new Date();
    if (this.location.pickupHours) {
      let pickupHours = this.location.pickupHours.find(hours => this.doesOverlap(hours.start, hours.end, now));
      if (!pickupHours) {
        pickupHours = this.location.pickupHours.find(hours => this.isSameDay(hours.start, now));
      }
      if (!pickupHours) {
        this.pickupStart = null;
        this.pickupEnd = null;
      } else {
        this.pickupStart = pickupHours.start;
        this.pickupEnd = pickupHours.end;
      }
    }
    if (this.location.dispatchHours) {
      let deliveryHours = this.location.dispatchHours.find(hours => this.doesOverlap(hours.start, hours.end, now));
      if (!deliveryHours) {
        deliveryHours = this.location.pickupHours.find(hours => this.isSameDay(hours.start, now));
      }
      if (!deliveryHours) {
        this.deliveryStart = null;
        this.deliveryEnd = null;
      } else {
        this.deliveryStart = deliveryHours.start;
        this.deliveryEnd = deliveryHours.end;
      }
    }
    if (this.pageFrom === 'details') {
      Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 10000 })
        .then(pos => {
          this.distance = this.geo.findDistance(
            this.location.address.latitude,
            this.location.address.longitude,
            pos.coords.latitude,
            pos.coords.longitude
          );
          this.distance = this.geo.precisionRound(this.distance, 1);
        })
        .catch(err => {
          console.log('Geolocation Error:', err);
        });
    }
  }

  private doesOverlap(start: Date, end: Date, query: Date): boolean {
    return new Date(start).getTime() <= new Date(query).getTime() && new Date(end).getTime() > new Date(query).getTime();
  }

  private isSameDay(dt1: Date, dt2: Date): boolean {
    return new Date(dt1).getDay() === new Date(dt2).getDay();
  }

  cateringClicked(event: MouseEvent): void {
    event.stopPropagation();
  }

  private determineHandoff() {
    /**
     * Returns the first TimeFrame that still intersects with "today."
     * For example, if "today" is Jan 10, then this checks if [start, end)
     * overlaps with [Jan 10 00:00, Jan 10 23:59].
     * This can handle crossing midnight, e.g.:
     *    start: 2025-01-09 18:00
     *    end:   2025-01-10 02:00
     * still "touches" Jan 10.
     */
    const findHoursForToday = (timeFrames: TimeFrame[]): TimeFrame | undefined => {
      const startOfToday = moment().startOf('day'); // e.g. Jan 10 00:00
      const endOfToday = moment().endOf('day'); // e.g. Jan 10 23:59

      // Find the first time frame that overlaps with today's date range at all
      return timeFrames.find(tf => {
        const start = moment(tf.start);
        const end = moment(tf.end);
        // Overlaps today if:
        // 1) it starts before the end of today, AND
        // 2) it ends after the start of today.
        return start.isBefore(endOfToday) && end.isAfter(startOfToday);
      });
    };

    this.handoffMethods = 'Offers ';
    this.unavailable = '';

    const methods: string[] = [];
    const unavailableMethods: string[] = [];

    // Check each handoff type and include those supported by the location
    if (this.location.supportsPickup && this.location.pickupHours && this.location.pickupHours.length) {
      let pickup = 'Pickup'; // Default label, will be replaced by the pipe in the template
      const pickupToday = findHoursForToday(this.location.pickupHours);

      if (pickupToday && moment().isAfter(pickupToday.end, 'minute') && this.location.isOpen) {
        unavailableMethods.push(pickup);
        pickup += '*';
      }
      methods.push(pickup);
    }

    if (this.location.supportsCurbside && this.location.curbsideHours && this.location.curbsideHours.length) {
      let curbside = 'Curbside'; // Default label, will be replaced by the pipe in the template
      const curbsideToday = findHoursForToday(this.location.curbsideHours);

      if (curbsideToday && moment().isAfter(curbsideToday.end, 'minute') && this.location.isOpen) {
        unavailableMethods.push(curbside);
        curbside += '*';
      }
      methods.push(curbside);
    }

    if (
      (this.location.supportsDispatch && this.location.dispatchHours && this.location.dispatchHours.length) ||
      (this.location.supportsDelivery && this.location.deliveryHours && this.location.deliveryHours.length)
    ) {
      let delivery = 'Delivery'; // Default label, will be replaced by the pipe in the template

      // For dispatch
      const dispatchToday =
        this.location.supportsDispatch && this.location.dispatchHours ? findHoursForToday(this.location.dispatchHours) : undefined;

      // For delivery
      const deliveryToday =
        this.location.supportsDelivery && this.location.deliveryHours ? findHoursForToday(this.location.deliveryHours) : undefined;

      const isDispatchPastEnd = dispatchToday && moment().isAfter(dispatchToday.end, 'minute');
      const isDeliveryPastEnd = deliveryToday && moment().isAfter(deliveryToday.end, 'minute') && this.location.isOpen;

      // If either is past today's timeframe, mark "Delivery" as unavailable
      if (isDispatchPastEnd || isDeliveryPastEnd) {
        unavailableMethods.push(delivery);
        delivery += '*';
      }
      methods.push(delivery);
    }

    if (this.location.supportsDriveThru && this.location.driveThruHours && this.location.driveThruHours.length) {
      let driveThru = 'Drive-Thru'; // Default label, will be replaced by the pipe in the template
      const driveThruToday = findHoursForToday(this.location.driveThruHours);

      if (driveThruToday && moment().isAfter(driveThruToday.end, 'minute') && this.location.isOpen) {
        unavailableMethods.push(driveThru);
        driveThru += '*';
      }
      methods.push(driveThru);
    }

    if (this.location.supportsTableside && this.location.tablesideHours && this.location.tablesideHours.length) {
      let tableside = 'Dine-In'; // Default label, will be replaced by the pipe in the template
      const tablesideToday = findHoursForToday(this.location.tablesideHours);

      if (tablesideToday && moment().isAfter(tablesideToday.end, 'minute') && this.location.isOpen) {
        unavailableMethods.push(tableside);
        tableside += '*';
      }
      methods.push(tableside);
    }

    if (this.location.cateringLink) {
      methods.push('Catering');
    }

    // Build "Offers ..." string
    if (methods.length === 1) {
      this.handoffMethods += methods[0];
    } else if (methods.length === 2) {
      this.handoffMethods += methods[0] + ' & ' + methods[1];
    } else if (methods.length >= 3) {
      methods.forEach((method: string, index: number) => {
        if (index < methods.length - 2) {
          this.handoffMethods += method + ', ';
        }
      });
      this.handoffMethods += methods[methods.length - 2] + ' & ' + methods[methods.length - 1];
    }

    // Build "unavailable" string
    if (unavailableMethods.length === 1) {
      this.unavailable += unavailableMethods[0];
    } else if (unavailableMethods.length === 2) {
      this.unavailable += unavailableMethods[0] + ' & ' + unavailableMethods[1];
    } else if (unavailableMethods.length >= 3) {
      unavailableMethods.forEach((method: string, index: number) => {
        if (index < unavailableMethods.length - 2) {
          this.unavailable += method + ', ';
        }
      });
      this.unavailable += unavailableMethods[unavailableMethods.length - 2] + ' & ' + unavailableMethods[unavailableMethods.length - 1];
    }

    if (unavailableMethods.length) {
      this.unavailable += ' ' + (unavailableMethods.length > 1 ? 'are' : 'is') + ' no longer available for today.';
    }
  }

  openNavigation(event: MouseEvent): void {
    event.stopPropagation();
    const preppedAddress = encodeURIComponent(this.location.address.address1.replace(/[ ]+/g, '+'));
    if (window) {
      window.open(
        'https://www.google.com/maps/dir/Current+Location/' +
          preppedAddress +
          '+' +
          this.location.address.state +
          '+' +
          this.location.address.zipCode,
        '_blank',
        'noopener=yes'
      );
    } else {
      document.location.href =
        'https://www.google.com/maps/dir/Current+Location/' +
        preppedAddress +
        '+' +
        this.location.address.state +
        '+' +
        this.location.address.zipCode;
    }
  }

  listCardLeft() {
    this.leftCard.emit();
  }

  cardHovered() {
    this.hoveredIndex.emit();
  }

  isHandoffUnavailableToday(handoffType: HandoffType): boolean {
    const findHoursForToday = (timeFrames: TimeFrame[]): TimeFrame | undefined => {
      const startOfToday = moment().startOf('day');
      const endOfToday = moment().endOf('day');
      return timeFrames.find(tf => {
        const start = moment(tf.start);
        const end = moment(tf.end);
        return start.isBefore(endOfToday) && end.isAfter(startOfToday);
      });
    };

    if (!this.location) {
      return false;
    }

    switch (handoffType) {
      case HandoffType.pickup:
        if (this.location.pickupHours) {
          const pickupToday = findHoursForToday(this.location.pickupHours);
          return !!pickupToday && moment().isAfter(pickupToday.end, 'minute');
        }
        return false;
      case HandoffType.curbside:
        if (this.location.curbsideHours) {
          const curbsideToday = findHoursForToday(this.location.curbsideHours);
          return !!curbsideToday && moment().isAfter(curbsideToday.end, 'minute');
        }
        return false;
      case HandoffType.delivery:
        if (this.location.deliveryHours) {
          const deliveryToday = findHoursForToday(this.location.deliveryHours);
          return !!deliveryToday && moment().isAfter(deliveryToday.end, 'minute');
        }
        return false;
      case HandoffType.dispatch:
        if (this.location.dispatchHours) {
          const dispatchToday = findHoursForToday(this.location.dispatchHours);
          return !!dispatchToday && moment().isAfter(dispatchToday.end, 'minute');
        }
        return false;
      case HandoffType.driveThru:
        if (this.location.driveThruHours) {
          const driveThruToday = findHoursForToday(this.location.driveThruHours);
          return !!driveThruToday && moment().isAfter(driveThruToday.end, 'minute');
        }
        return false;
      case HandoffType.dineIn:
        if (this.location.tablesideHours) {
          const tablesideToday = findHoursForToday(this.location.tablesideHours);
          return !!tablesideToday && moment().isAfter(tablesideToday.end, 'minute');
        }
        return false;
      default:
        return false;
    }
  }

  getSupportedHandoffTypes(): { type: HandoffType; available: boolean }[] {
    if (!this.location) {
      return [];
    }

    const result: { type: HandoffType; available: boolean }[] = [];

    // Check each handoff type
    if (this.location.supportsPickup && this.location.pickupHours && this.location.pickupHours.length) {
      result.push({
        type: HandoffType.pickup,
        available: !(this.location.isOpen && this.isHandoffUnavailableToday(HandoffType.pickup)),
      });
    }

    if (this.location.supportsCurbside && this.location.curbsideHours && this.location.curbsideHours.length) {
      result.push({
        type: HandoffType.curbside,
        available: !(this.location.isOpen && this.isHandoffUnavailableToday(HandoffType.curbside)),
      });
    }

    if (this.location.supportsDispatch && this.location.dispatchHours && this.location.dispatchHours.length) {
      result.push({
        type: HandoffType.dispatch,
        available: !(this.location.isOpen && this.isHandoffUnavailableToday(HandoffType.dispatch)),
      });
    } else if (this.location.supportsDelivery && this.location.deliveryHours && this.location.deliveryHours.length) {
      result.push({
        type: HandoffType.delivery,
        available: !(this.location.isOpen && this.isHandoffUnavailableToday(HandoffType.delivery)),
      });
    }

    if (this.location.supportsDriveThru && this.location.driveThruHours && this.location.driveThruHours.length) {
      result.push({
        type: HandoffType.driveThru,
        available: !(this.location.isOpen && this.isHandoffUnavailableToday(HandoffType.driveThru)),
      });
    }

    if (this.location.supportsTableside && this.location.tablesideHours && this.location.tablesideHours.length) {
      result.push({
        type: HandoffType.dineIn,
        available: !(this.location.isOpen && this.isHandoffUnavailableToday(HandoffType.dineIn)),
      });
    }

    return result;
  }

  getUnavailableHandoffTypes(): HandoffType[] {
    return this.getSupportedHandoffTypes()
      .filter(item => !item.available)
      .map(item => item.type);
  }
}
